import * as React from "react";
import { Button, makeStyles, tokens } from "@fluentui/react-components";
import { ErrorCircle24Regular } from "@fluentui/react-icons";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <DefaultErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

interface DefaultErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
}

const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({ error, onReset }) => {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <ErrorCircle24Regular className={styles.icon} />
        <h2 className={styles.title}>Something went wrong</h2>
        <p className={styles.message}>
          An unexpected error occurred. Please try refreshing the add-in.
        </p>
        {error && (
          <details className={styles.details}>
            <summary className={styles.summary}>Error details</summary>
            <pre className={styles.errorText}>{error.message}</pre>
          </details>
        )}
        <Button appearance="primary" onClick={onReset} className={styles.button}>
          Try again
        </Button>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    padding: "20px",
  },
  content: {
    textAlign: "center",
    maxWidth: "400px",
  },
  icon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: "48px",
    marginBottom: "16px",
  },
  title: {
    fontSize: "20px",
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: "8px",
    color: tokens.colorNeutralForeground1,
  },
  message: {
    fontSize: "14px",
    color: tokens.colorNeutralForeground2,
    marginBottom: "16px",
    lineHeight: "1.5",
  },
  details: {
    marginBottom: "16px",
    textAlign: "left",
    backgroundColor: tokens.colorNeutralBackground3,
    padding: "12px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  summary: {
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: "8px",
    color: tokens.colorNeutralForeground3,
  },
  errorText: {
    fontSize: "11px",
    fontFamily: tokens.fontFamilyMonospace,
    color: tokens.colorPaletteRedForeground2,
    overflow: "auto",
    maxHeight: "150px",
    margin: 0,
  },
  button: {
    marginTop: "8px",
  },
});
